import { z } from 'zod';
import { zIsoDate, zNonEmpty, zUuid } from '@saas/shared';

/**
 * crm の公開 DTO（02 §6.2 v5.2・03 §17.3・§18.2）。
 * 顧客（customers）・連絡先（contacts = 名刺 1 枚 1 人）・連絡先の識別子（contact_identifiers）・
 * 先方の部署（customer_departments）・統合タイムライン（activities）。
 * 名刺は人手確認（confirmed_at）が済むまで「登録済み」として見せない。
 */

// ---- 列挙 ----

/** 電話・メール等の種別（03 §17.3）。display_name / email_domain はサーバが導出する（入力しない） */
export const IDENTIFIER_KINDS = ['phone', 'mobile', 'fax', 'email', 'display_name', 'email_domain'] as const;
export type IdentifierKind = (typeof IDENTIFIER_KINDS)[number];
/** 画面で入力できる種別 */
export const INPUT_IDENTIFIER_KINDS = ['phone', 'mobile', 'fax', 'email'] as const;
export type InputIdentifierKind = (typeof INPUT_IDENTIFIER_KINDS)[number];

/** company = 会社を指す値（代表番号・info@）/ personal = 本人を名指しする値。既定は安全側の company（03 §17.3） */
export const IDENTIFIER_SCOPES = ['personal', 'company'] as const;
export type IdentifierScope = (typeof IDENTIFIER_SCOPES)[number];

export const IDENTIFIER_SOURCES = ['card_ocr', 'manual', 'calendar', 'mail_signature'] as const;
export type IdentifierSource = (typeof IDENTIFIER_SOURCES)[number];

/** 意思決定役割（03 §18.2・§18.3）。営業が手で入れる */
export const DECISION_ROLES = ['decision_maker', 'champion', 'gatekeeper', 'influencer', 'user', 'blocker'] as const;
export type DecisionRole = (typeof DECISION_ROLES)[number];

/** 活動の種類（02 §6.2）。card = 名刺交換（確認時に自動記録） */
export const ACTIVITY_TYPES = ['call', 'email', 'visit', 'note', 'file', 'meeting', 'card'] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export const CARD_OCR_STATUSES = ['awaiting_upload', 'pending', 'done', 'failed', 'skipped'] as const;
export type CardOcrStatus = (typeof CARD_OCR_STATUSES)[number];

/** 先方の部署の由来。calendar（gap-crm-a の追加）= 予定の社外参加者の表示名から採用した部署 */
export const DEPARTMENT_SOURCES = ['manual', 'card_ocr', 'signature', 'web', 'calendar'] as const;
export type DepartmentSource = (typeof DEPARTMENT_SOURCES)[number];

// ---- サマリ DTO（公開 I/F はこれだけを返す）----

export interface CustomerSummary {
  id: string;
  companyName: string;
  companyNameKana: string | null;
  ownerId: string | null;
  ownerName: string | null;
  address: string | null;
  website: string | null;
  industry: string | null;
  tags: string[];
  /** 確認済み・在籍中の連絡先の数 */
  contactCount: number;
  lastActivityAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface CustomerDepartmentSummary {
  id: string;
  customerId: string;
  parentId: string | null;
  name: string;
  /** LTREE の文字列（ラベルは id のハイフン除去） */
  path: string;
  depth: number;
  source: DepartmentSource;
  contactCount: number;
}

/** 意思決定マップ（03 §18.3）。reports_to で木にする */
export interface DecisionMapNode {
  contactId: string;
  name: string;
  title: string | null;
  departmentName: string | null;
  decisionRole: DecisionRole | null;
  influence: number | null;
  reportsToContactId: string | null;
  leftCompanyAt: string | null;
  /** 名刺交換以外の接点（活動）があるか。決裁者・門番に無ければ危険（§18.3） */
  hasActivity: boolean;
  children: DecisionMapNode[];
}

export interface CustomerDetail extends CustomerSummary {
  customFields: Record<string, unknown>;
  /** テナントが定義したカスタム項目（編集フォームに出す。段 5 で追加） */
  customFieldDefs: CustomFieldDef[];
  departments: CustomerDepartmentSummary[];
  decisionMap: DecisionMapNode[];
  /** 決裁者・門番のうち接点が無い人（失注の最大の予兆・§18.3） */
  unreachedRoles: Array<{ contactId: string; name: string; decisionRole: DecisionRole }>;
  actions: { edit: boolean; delete: boolean; logActivity: boolean; manageDepartments: boolean };
}

export interface ContactIdentifierSummary {
  id: string;
  kind: IdentifierKind;
  scope: IdentifierScope;
  /** 正規化済み（電話は E.164、メールは小文字、氏名は空白除去） */
  value: string;
  /** 名刺の表記そのまま（「代表」「直通」「工場」） */
  label: string | null;
  isPrimary: boolean;
  source: IdentifierSource | null;
  verifiedAt: Date | null;
}

export interface ContactSummary {
  id: string;
  customerId: string | null;
  customerName: string | null;
  name: string;
  nameKana: string | null;
  title: string | null;
  /** 名刺の部署の文字列（構造化した customerDepartmentId とは別） */
  departmentName: string | null;
  customerDepartmentId: string | null;
  customerDepartmentName: string | null;
  decisionRole: DecisionRole | null;
  influence: number | null;
  reportsToContactId: string | null;
  leftCompanyAt: string | null;
  ownerId: string | null;
  ownerName: string | null;
  exchangedOn: string | null;
  confirmedAt: Date | null;
  cardFileId: string | null;
  primaryPhone: string | null;
  primaryEmail: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface ContactDetail extends ContactSummary {
  address: string | null;
  website: string | null;
  exchangedBy: string | null;
  exchangedByName: string | null;
  confirmedBy: string | null;
  reportsToName: string | null;
  identifiers: ContactIdentifierSummary[];
  ocr: { status: CardOcrStatus; confidence: string | null; fieldConfidence: Record<string, number> } | null;
  /** 2 枚目以降の名刺（decisions.md §F-5）。新しい順 */
  cardHistory: CardHistoryEntry[];
  actions: { edit: boolean; delete: boolean; logActivity: boolean };
  /**
   * （gap-crm-a の追加・任意）連絡先のカスタム項目の値（key => value）と、会社が定義した連絡先の項目（編集フォームに出す）。
   * 任意なのは古いサーバ・モバイルの型との互換のため（無ければ空として扱う）
   */
  customFields?: Record<string, unknown>;
  customFieldDefs?: CustomFieldDef[];
}

/** OCR が名刺から読む項目（CARD-03 の 12 項目・05 §5.9）。すべて「候補」で、人手確認で確定する */
export interface CardOcrFields {
  companyName?: string;
  companyNameKana?: string;
  name?: string;
  nameKana?: string;
  title?: string;
  departmentName?: string;
  address?: string;
  website?: string;
  phones?: Array<{ kind: InputIdentifierKind; value: string; label?: string }>;
  emails?: string[];
}

/** 確認キュー（CARD-02）の 1 行 = 未確認の contact */
export interface CardSummary {
  id: string;
  cardFileId: string | null;
  ocrStatus: CardOcrStatus;
  ocrConfidence: string | null;
  exchangedOn: string | null;
  exchangedBy: string | null;
  exchangedByName: string | null;
  ownerId: string | null;
  /** OCR 候補の氏名・会社名（一覧の見出し用。未読み取りなら null） */
  suggestedName: string | null;
  suggestedCompany: string | null;
  createdAt: Date;
}

/** SFA 段階 5（§9.3）: 確認待ちの列の中で同じメール / 携帯を持つ別の名刺（同じ人を 2 回取り込んだ疑い） */
export interface CardQueueDuplicate {
  id: string;
  suggestedName: string | null;
  suggestedCompany: string | null;
  exchangedOn: string | null;
  exchangedByName: string | null;
  matchReason: 'email' | 'mobile';
}

export interface CardDetail extends CardSummary {
  suggestions: CardOcrFields;
  fieldConfidence: Record<string, number>;
  ocrError: string | null;
  /**
   * SFA 段階 5（§9.3）: 読み取りの状態の出し分け。
   *   ocrAvailable … この会社で今 OCR が動くか（事業者が設定済み ∧ 会社の opt-out（settings.features['crm.cardOcr'] = false）でない）
   *   ocrErrorCode … failed の理由（unsupported_format / too_large / auth / rate_limited / timeout / vendor_error / file_missing）、
   *                  done で文字が無かった no_text、skipped の理由（unavailable / disabled / plan_limit / confirmed）
   *   ocrProvider … 読んだ事業者（stub / fake / google_vision / azure_read）
   *   lowConfidenceThreshold … この値未満の項目に warning（web / mobile で揃える。0.7）
   *   ocrRereadsRemaining … 手動の再読み取りの残り回数（0 なら 403 card_reread_limit）
   */
  ocrAvailable: boolean;
  ocrErrorCode: string | null;
  ocrProvider: string | null;
  lowConfidenceThreshold: number;
  ocrRereadsRemaining: number;
  /** 確認待ちの列の中の同じメール / 携帯の名刺（見える範囲だけ） */
  queueDuplicates: CardQueueDuplicate[];
  /** 既に入力済みの下書き（確認前に保存された値）。suggestions より優先して表示する */
  draft: {
    customerId: string | null;
    /** customerId の会社名（画面の取引先ピッカーに出すため） */
    customerName: string | null;
    name: string;
    nameKana: string | null;
    title: string | null;
    departmentName: string | null;
    address: string | null;
    website: string | null;
  };
  /** CARD-04: メールドメイン・代表番号・会社名で当たった既存顧客 */
  duplicateCandidates: CustomerSummary[];
  /** §F-5: 同一人物（2 枚目の名刺）の候補。メール一致 > 氏名 + 会社一致 */
  sameContactCandidates: CardMergeCandidate[];
  /** CARD-03 の「前へ / 次へ」（確認キューの隣） */
  prevId: string | null;
  nextId: string | null;
  /** キューに残っている枚数（自分に見える範囲） */
  remaining: number;
}

/** 名刺のアップロード開始の戻り（core.FileService の署名付き PUT） */
export interface CardUploadTicket {
  contactId: string;
  fileId: string;
  uploadUrl: string;
  expiresAt: Date;
}

export interface ActivitySummary {
  id: string;
  customerId: string | null;
  contactId: string | null;
  contactName: string | null;
  userId: string | null;
  userName: string | null;
  type: ActivityType;
  refType: string | null;
  refId: string | null;
  body: string | null;
  occurredAt: Date;
  createdAt: Date;
  /**
   * SFA 1A（§15.2.3 活動の統一モデル）の追加列。既存の行・既存の呼び出し側では null / 省略。
   * 任意にしてあるのは、他モジュールのテストダブル（ActivitySummary を組み立てるもの）を壊さないため
   */
  direction?: 'outbound' | 'inbound' | null;
  outcome?: 'reached' | 'no_answer' | 'replied' | 'none' | null;
  inquiryId?: string | null;
  leadId?: string | null;
  dealId?: string | null;
  /** SFA 段階 5（§9.4）: 通話の長さ（秒）。電話以外は null */
  durationSec?: number | null;
  /** （gap-crm-a の追加・任意）予定（calendar）から記録した活動ならその予定 ID。日報の二重計上を防ぐ照合に使う */
  calendarEventId?: string | null;
}

/** 公開 I/F の logActivity の入力（tx は呼び出し側のもの） */
export interface LogActivityInput {
  customerId?: string | null;
  contactId?: string | null;
  /** 自社側の担当（記録者）。ジョブ・システムは null */
  userId?: string | null;
  type: ActivityType;
  body?: string | null;
  occurredAt?: Date;
  /** 元レコード（calls.id など）。渡すと同じ参照の 2 回目は既存の行を返す */
  refType?: string | null;
  refId?: string | null;
  /** 監査ログ・Outbox の actor */
  actorId?: string | null;
  /**
   * SFA 1A（§15.2.3）: 方向・結果・関連する商談・見込み客。省略すると null（従来どおり）。
   * leadId だけでも記録できる（取引先・連絡先が未確定の見込み客の活動）
   */
  direction?: 'outbound' | 'inbound' | null;
  outcome?: 'reached' | 'no_answer' | 'replied' | 'none' | null;
  dealId?: string | null;
  leadId?: string | null;
  /**
   * SFA 段階 5（§9.4）: 通話の長さ（秒）と、見込み客の問い合わせへの紐づけ。inquiryId = 'auto' は「未対応の問い合わせが 1 件だけならそれ」
   * （複数なら紐づけない。初回対応の判定は countsAsFirstResponse の規則どおり）
   */
  durationSec?: number | null;
  inquiryId?: string | 'auto' | null;
  /**
   * （gap-crm-a の追加・共有の取り決め）予定（calendar）から記録した活動の予定 ID。保存して `crm.activity.logged` の calendarEventId に載せる。
   * 省略時は refType = 'calendar_event' なら refId を使う（予定の紐づけ・予定の終了から作る活動）。crm は calendar を呼べないので、ID の実在は確かめない
   */
  calendarEventId?: string | null;
}

// ---- 入力 DTO（Zod）----

const zOptionalText = (max: number) => z.string().trim().max(max).nullable().optional();
const zTags = z.array(z.string().trim().min(1).max(50)).max(50);
/** クエリ文字列の真偽値（"1" / "true" だけ true）。z.coerce.boolean() は "false" も true にしてしまうので使わない */
const zQueryBool = z.preprocess((v) => (typeof v === 'string' ? v === '1' || v.toLowerCase() === 'true' : v), z.boolean()).optional();

export const zCreateCustomer = z.object({
  companyName: zNonEmpty.max(200),
  companyNameKana: zOptionalText(200),
  ownerId: zUuid.nullable().optional(),
  address: zOptionalText(500),
  website: zOptionalText(500),
  industry: zOptionalText(100),
  tags: zTags.optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateCustomerInput = z.infer<typeof zCreateCustomer>;
export const zUpdateCustomer = zCreateCustomer.partial();
export type UpdateCustomerInput = z.infer<typeof zUpdateCustomer>;

export const zCustomerListQuery = z.object({
  q: z.string().trim().max(100).optional(),
  ownerId: zUuid.optional(),
  tag: z.string().trim().max(50).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type CustomerListQuery = z.infer<typeof zCustomerListQuery>;

/** CARD-04 の重複判定: 会社を指す値（メールドメイン・代表番号）と会社名 */
export const zCustomerMatchQuery = z.object({
  emailDomain: z.string().trim().max(200).optional(),
  phone: z.string().trim().max(50).optional(),
  companyName: z.string().trim().max(200).optional(),
});
export type CustomerMatchQuery = z.infer<typeof zCustomerMatchQuery>;

export const zIdentifierInput = z.object({
  kind: z.enum(INPUT_IDENTIFIER_KINDS),
  value: zNonEmpty.max(200),
  label: zOptionalText(50),
  /** 既定は安全側（会社）。本人の直通・携帯だけ personal に上げる（05 §5.9a） */
  scope: z.enum(IDENTIFIER_SCOPES).default('company'),
  isPrimary: z.boolean().optional(),
});
export type IdentifierInput = z.infer<typeof zIdentifierInput>;

const zContactFields = {
  customerId: zUuid.nullable().optional(),
  name: zNonEmpty.max(100),
  nameKana: zOptionalText(100),
  title: zOptionalText(100),
  departmentName: zOptionalText(100),
  address: zOptionalText(500),
  website: zOptionalText(500),
  customerDepartmentId: zUuid.nullable().optional(),
  reportsToContactId: zUuid.nullable().optional(),
  decisionRole: z.enum(DECISION_ROLES).nullable().optional(),
  influence: z.number().int().min(1).max(5).nullable().optional(),
  ownerId: zUuid.nullable().optional(),
  exchangedOn: zIsoDate.nullable().optional(),
  /**
   * （gap-crm-a の追加）連絡先のカスタム項目（会社が定義した項目の key => value）。編集では送れば丸ごと置き換える（取引先と同じ）。
   * 型・選択肢はサーバが検査する（定義の無いキーは通す = 取込で入った値を守る）
   */
  customFields: z.record(z.string(), z.unknown()).optional(),
};

/** 手入力の連絡先（名刺なし）。作成時点で確認済みになる */
export const zCreateContact = z.object({
  ...zContactFields,
  identifiers: z.array(zIdentifierInput).max(20).default([]),
});
export type CreateContactInput = z.infer<typeof zCreateContact>;

export const zUpdateContact = z
  .object({
    ...zContactFields,
    /** 退職の取り消しは null */
    leftCompanyAt: zIsoDate.nullable().optional(),
  })
  .partial();
export type UpdateContactInput = z.infer<typeof zUpdateContact>;

export const zLeaveContact = z.object({ leftCompanyAt: zIsoDate });
export type LeaveContactInput = z.infer<typeof zLeaveContact>;

export const zContactListQuery = z.object({
  /** 氏名・かな・会社名、または電話番号 / メールアドレス（identifiers の逆引き） */
  q: z.string().trim().max(200).optional(),
  customerId: zUuid.optional(),
  customerDepartmentId: zUuid.optional(),
  decisionRole: z.enum(DECISION_ROLES).optional(),
  /** 退職した人も含める（既定は在籍中だけ） */
  includeLeft: zQueryBool,
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type ContactListQuery = z.infer<typeof zContactListQuery>;

// ---- 名刺（CARD-01〜03）----

export const zStartCardUpload = z.object({
  filename: zNonEmpty.max(255),
  mime: z.string().regex(/^image\/[a-z0-9.+-]+$/i, '名刺は画像ファイルで登録してください'),
  sizeBytes: z.number().int().positive().max(50 * 1024 * 1024),
  /** 受け取った日。既定は今日（撮影日） */
  exchangedOn: zIsoDate.optional(),
  customerId: zUuid.nullable().optional(),
});
export type StartCardUploadInput = z.infer<typeof zStartCardUpload>;

export const zCompleteCardUpload = z.object({
  contentHash: z.string().regex(/^[0-9a-f]{64}$/, 'contentHash は SHA-256 の 16 進 64 文字'),
  sizeBytes: z.number().int().positive(),
  scanResult: z.enum(['clean', 'infected', 'skipped']).default('skipped'),
});
export type CompleteCardUploadInput = z.infer<typeof zCompleteCardUpload>;

/** CARD-03 の確認。会社は既存（customerId）か新規（companyName）か無し */
export const zConfirmCard = z.object({
  customerId: zUuid.nullable().optional(),
  companyName: zOptionalText(200),
  companyNameKana: zOptionalText(200),
  name: zNonEmpty.max(100),
  nameKana: zOptionalText(100),
  title: zOptionalText(100),
  departmentName: zOptionalText(100),
  address: zOptionalText(500),
  website: zOptionalText(500),
  exchangedOn: zIsoDate,
  identifiers: z.array(zIdentifierInput).max(20).default([]),
  /** 名刺の部署名から先方の部署（customer_departments）も作る */
  createDepartment: z.boolean().default(false),
});
export type ConfirmCardInput = z.infer<typeof zConfirmCard>;

/** 確認前の下書き保存（次へ / 前へで途中の値を失わない） */
export const zSaveCardDraft = zConfirmCard.omit({ identifiers: true, createDepartment: true, exchangedOn: true }).partial().extend({ exchangedOn: zIsoDate.optional() });
export type SaveCardDraftInput = z.infer<typeof zSaveCardDraft>;

export const zCardListQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type CardListQuery = z.infer<typeof zCardListQuery>;

// ---- 先方の部署（03 §18.2）----

export const zCreateDepartment = z.object({
  name: zNonEmpty.max(100),
  parentId: zUuid.nullable().optional(),
  /** 由来（既定 manual）。名刺の確認からは card_ocr */
  source: z.enum(DEPARTMENT_SOURCES).optional(),
});
export type CreateDepartmentInput = z.infer<typeof zCreateDepartment>;
export const zUpdateDepartment = z.object({
  name: zNonEmpty.max(100).optional(),
  /** 親を変える（配下ごと移動）。null でトップレベルへ */
  parentId: zUuid.nullable().optional(),
});
export type UpdateDepartmentInput = z.infer<typeof zUpdateDepartment>;

// ---- 活動（統合タイムライン）----

export const zLogActivity = z
  .object({
    customerId: zUuid.nullable().optional(),
    contactId: zUuid.nullable().optional(),
    type: z.enum(ACTIVITY_TYPES),
    body: z.string().trim().max(5000).nullable().optional(),
    occurredAt: z.string().datetime({ offset: true }).optional(),
    /**
     * （crm-dormant の追加・§15.2.3）方向と結果。取引先の「最終接点」（休眠の判定）に数える電話は outcome = reached だけ。
     * 省略 = null（従来どおり。結果の分からない電話は最終接点に数えない）。値は crm-lead.dto.ts の ACTIVITY_DIRECTIONS / ACTIVITY_OUTCOMES と同じ
     */
    direction: z.enum(['outbound', 'inbound']).nullable().optional(),
    outcome: z.enum(['reached', 'no_answer', 'replied', 'none']).nullable().optional(),
    /** SFA 段階 5（§9.5）: 関連する商談（読める商談だけ。無効なら 400）と通話の長さ */
    dealId: zUuid.nullable().optional(),
    durationSec: z.number().int().min(0).max(86400).nullable().optional(),
    /** （gap-crm-a の追加）予定から記録した活動の予定 ID（日報の二重計上の照合用。実在は確かめない） */
    calendarEventId: zUuid.nullable().optional(),
  })
  .refine((v) => Boolean(v.customerId || v.contactId), { message: '顧客または連絡先を指定してください', path: ['customerId'] });
export type LogActivityHttpInput = z.infer<typeof zLogActivity>;

export const zActivityListQuery = z.object({
  customerId: zUuid.optional(),
  contactId: zUuid.optional(),
  type: z.enum(ACTIVITY_TYPES).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type ActivityListQuery = z.infer<typeof zActivityListQuery>;

// ---- 段 5: CSV の書き出しと取込（03 第 I 部 §7・01 L895。導入の入口）----

export const IMPORT_KINDS = ['customer', 'contact'] as const;
export type ImportKind = (typeof IMPORT_KINDS)[number];

/** 行ごとの扱い。既定は「重複ならスキップ」（勝手に上書きしない） */
export const IMPORT_ROW_MODES = ['create', 'update', 'skip'] as const;
export type ImportRowMode = (typeof IMPORT_ROW_MODES)[number];

/**
 * 1 回の上限（行 / バイト）。プレビューに詳しく出す行数。
 * バイト上限は base64（約 4/3 倍）にしても apps/api の JSON 上限（8MB）の内側に収まる大きさにする
 */
export const IMPORT_MAX_ROWS = 5000;
export const IMPORT_MAX_BYTES = 5_000_000;
export const IMPORT_PREVIEW_ROWS = 20;
/** 画面・エラー文言に出す上限の表記 */
export const IMPORT_MAX_SIZE_LABEL = `${IMPORT_MAX_BYTES / 1_000_000}MB`;
/** プレビューで返す重複行・エラー行の上限（全部返すと巨大になる） */
export const IMPORT_PREVIEW_EXTRA = 100;
/** 1 トランザクションで処理する行数（行ごとに tx を長く持たない） */
export const IMPORT_CHUNK_ROWS = 500;

export interface ImportFieldInfo {
  key: string;
  label: string;
  required: boolean;
  /** 評価（事実ではない）。03 §18.7 */
  assessment: boolean;
  hint: string | null;
  /** （gap-crm-a の追加・任意）会社が定義したカスタム項目の列（key は `cf.<項目キー>`） */
  custom?: boolean;
}

export interface ImportRowIssue {
  field: string | null;
  message: string;
}

export interface ImportPreviewRow {
  /** CSV の行番号（見出しを 1 行目とした 1 始まり） */
  rowNumber: number;
  values: Record<string, string>;
  status: 'new' | 'duplicate' | 'error';
  /** 既定の扱い（duplicate は skip）。画面で行ごとに変えられる */
  mode: ImportRowMode;
  matchedId: string | null;
  matchedLabel: string | null;
  /** 「法人番号が一致」「メールが一致」など */
  matchReason: string | null;
  issues: ImportRowIssue[];
}

export interface ImportPreview {
  kind: ImportKind;
  filename: string;
  encoding: string;
  encodingLabel: string;
  headers: string[];
  /** headers と同じ長さ。null = 取り込まない列 */
  mapping: Array<string | null>;
  fields: ImportFieldInfo[];
  totalRows: number;
  summary: { total: number; new: number; duplicate: number; error: number };
  /** 先頭 20 行 + 重複行・エラー行（各 100 件まで）を行番号順に */
  rows: ImportPreviewRow[];
  /** 5,000 行を超えていて切り捨てた */
  truncated: boolean;
}

export interface ImportResult {
  importId: string;
  kind: ImportKind;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ rowNumber: number; message: string }>;
}

export interface ImportSummary {
  id: string;
  kind: ImportKind;
  filename: string;
  encoding: string;
  status: 'completed' | 'undone';
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: Date;
  undoneAt: Date | null;
  /** 取り消せるか（作った行があり、まだ取り消していない） */
  canUndo: boolean;
}

const zBase64Csv = z
  .string()
  .min(1, 'CSV ファイルを選んでください')
  // base64 は 3 バイト → 4 文字。余裕を見て + 64
  .max(Math.ceil(IMPORT_MAX_BYTES / 3) * 4 + 64, `CSV は ${IMPORT_MAX_SIZE_LABEL} までです。行を分けて取り込んでください`);

export const zImportPreview = z.object({
  kind: z.enum(IMPORT_KINDS),
  filename: zNonEmpty.max(255),
  /** ファイルの中身（base64）。文字コードはサーバが判定する */
  contentBase64: zBase64Csv,
  /** 列の対応づけ（headers と同じ長さ）。省略すると見出しから自動で推定する */
  mapping: z.array(z.string().max(50).nullable()).max(200).optional(),
});
export type ImportPreviewInput = z.infer<typeof zImportPreview>;

export const zImportCommit = zImportPreview.extend({
  /** プレビューで指定しなかった重複行の既定（skip = 何もしない / update = 既存を更新） */
  defaultMode: z.enum(['skip', 'update']).default('skip'),
  /** 行ごとの指定（プレビューに出た行）。create = 重複でも新規で作る */
  rowDecisions: z.array(z.object({ rowNumber: z.number().int().positive(), mode: z.enum(IMPORT_ROW_MODES) })).max(IMPORT_MAX_ROWS).default([]),
});
export type ImportCommitInput = z.infer<typeof zImportCommit>;

export const zImportListQuery = z.object({ limit: z.coerce.number().int().min(1).max(50).default(20) });
export type ImportListQuery = z.infer<typeof zImportListQuery>;

export const zSampleCsvQuery = z.object({ kind: z.enum(IMPORT_KINDS) });
export type SampleCsvQuery = z.infer<typeof zSampleCsvQuery>;

/** 書き出し（一覧と同じ絞り込み・見える範囲だけ） */
export const zCustomerExportQuery = zCustomerListQuery.omit({ limit: true, cursor: true }).extend({ includeAssessment: zQueryBool });
export type CustomerExportQuery = z.infer<typeof zCustomerExportQuery>;
export const zContactExportQuery = zContactListQuery.omit({ limit: true, cursor: true }).extend({ includeAssessment: zQueryBool });
export type ContactExportQuery = z.infer<typeof zContactExportQuery>;

// ---- 段 5: 顧客のカスタム項目（02 §6.2 customers.custom_fields の定義・gap A18）----

export const CUSTOM_FIELD_TYPES = ['text', 'number', 'date', 'select'] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

/** （gap-crm-a の追加）カスタム項目の対象。customer = 取引先（customers.custom_fields）/ contact = 連絡先（contacts.custom_fields） */
export const CUSTOM_FIELD_ENTITIES = ['customer', 'contact'] as const;
export type CustomFieldEntity = (typeof CUSTOM_FIELD_ENTITIES)[number];

export interface CustomFieldDef {
  id: string;
  key: string;
  label: string;
  type: CustomFieldType;
  options: string[];
  required: boolean;
  sortOrder: number;
  /** （gap-crm-a の追加・任意）対象。省略は customer（古いサーバ） */
  entity?: CustomFieldEntity;
}

/** GET /crm/custom-fields?entity=contact（gap-crm-a の追加。省略 = customer で従来どおり） */
export const zCustomFieldListQuery = z.object({ entity: z.enum(CUSTOM_FIELD_ENTITIES).default('customer') });
export type CustomFieldListQuery = z.infer<typeof zCustomFieldListQuery>;

export const zCreateCustomField = z.object({
  key: z
    .string()
    .trim()
    .regex(/^[a-zA-Z][a-zA-Z0-9_]{0,39}$/, '英字で始まる英数字と _ の 40 文字までで指定してください'),
  label: zNonEmpty.max(50),
  type: z.enum(CUSTOM_FIELD_TYPES),
  options: z.array(z.string().trim().min(1).max(50)).max(50).default([]),
  required: z.boolean().default(false),
  sortOrder: z.number().int().min(0).max(999).default(0),
  /** （gap-crm-a の追加）対象。省略 = customer。連絡先の項目の設定には crm.contact.manage.all が要る */
  entity: z.enum(CUSTOM_FIELD_ENTITIES).optional(),
});
export type CreateCustomFieldInput = z.infer<typeof zCreateCustomField>;
/** 対象（entity）と key は変えられない（値の対応が壊れる） */
export const zUpdateCustomField = zCreateCustomField.omit({ key: true, entity: true }).partial();
export type UpdateCustomFieldInput = z.infer<typeof zUpdateCustomField>;

// ---- 段 5: 名刺の 2 枚目の統合（decisions.md §F-5）----

export interface CardFieldChange {
  field: string;
  label: string;
  from: string | null;
  to: string;
  /** 統合のときに反映したか（履歴に残すときだけ入る） */
  applied?: boolean;
}

/** 同一人物の候補（メール一致 > 氏名 + 会社一致） */
export interface CardMergeCandidate {
  contactId: string;
  name: string;
  customerId: string | null;
  customerName: string | null;
  title: string | null;
  departmentName: string | null;
  /** email = 本人のメールが一致 / mobile = 本人の携帯が一致（SFA 段階 5）/ name_company = 氏名と会社が一致 */
  matchReason: 'email' | 'mobile' | 'name_company';
  /** 異動の検知: 部署・役職・電話が変わっている項目（自動では上書きしない） */
  changes: CardFieldChange[];
}

/** 名刺の履歴（contacts.card_history）。2 枚目以降の名刺を人に紐づけて残す */
export interface CardHistoryEntry {
  at: string;
  fileId: string | null;
  exchangedOn: string | null;
  exchangedBy: string | null;
  fields: Record<string, string>;
  changes: CardFieldChange[];
}

export const zMergeCard = z.object({
  /** 統合先の既存の連絡先 */
  targetContactId: zUuid,
  /** 反映する項目（title / departmentName / address / website）。渡さなければ履歴に残すだけ */
  applyFields: z.array(z.enum(['title', 'departmentName', 'address', 'website'])).max(8).default([]),
  /** 名刺の電話・メールを既存の連絡先に足す */
  identifiers: z.array(zIdentifierInput).max(20).default([]),
  exchangedOn: zIsoDate,
});
export type MergeCardInput = z.infer<typeof zMergeCard>;

// ---- 段 5: 事実と評価の分離（03 §18.7・gap A16）----

/**
 * 連絡先の「事実」だけ（氏名・部署・役職・連絡先）。評価（decision_role / influence / メモ）は含めない。
 * 開示請求（relationship の contact 単位の開示）や他モジュールへの受け渡しはこちらを使う
 */
export interface ContactFacts {
  id: string;
  customerId: string | null;
  customerName: string | null;
  name: string;
  nameKana: string | null;
  title: string | null;
  departmentName: string | null;
  customerDepartmentName: string | null;
  address: string | null;
  website: string | null;
  exchangedOn: string | null;
  leftCompanyAt: string | null;
  identifiers: Array<{ kind: IdentifierKind; scope: IdentifierScope; value: string; label: string | null; isPrimary: boolean }>;
}
